import { InlineFeedback, useFeedback } from '@mts/ui/feedback';

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Button, Card, Input, PageHeader, PublicContainer } from '@mts/ui';
import { Camera, CheckCircle2, Gift, Mail, Megaphone, Pencil, ShieldCheck, Sparkles, TrendingUp, UserRound } from 'lucide-react';
import { fetchListingBySlug, getApiBaseUrl } from '../services/api';
import { requestListingClaim } from '../services/claimFlow';
import { authClient } from '../lib/authClient';
import { resetPostHogIdentity } from '../lib/posthog';
import { fetchOwnProfile } from '../services/userProfiles';
import { formatPhoneNumber } from '../utils/phone';
import PromotionBanner from '../components/promotion/PromotionBanner';

const ROLE_OPTIONS = [
  { value: '', label: 'Select your role' },
  { value: 'owner', label: 'Owner' },
  { value: 'manager', label: 'Manager' },
  { value: 'employee', label: 'Employee' },
  { value: 'authorized_representative', label: 'Authorized representative' },
  { value: 'other', label: 'Other' },
];

function normalizeText(value) {
  return String(value || '').trim();
}

function buildListingAddress(listing) {
  const explicitAddress = normalizeText(listing?.address);
  if (explicitAddress) return explicitAddress;

  return [
    listing?.addressLine1,
    listing?.addressLine2,
    listing?.city,
    listing?.state,
    listing?.postalCode,
  ]
    .map(normalizeText)
    .filter(Boolean)
    .join(', ');
}

function titleizeSlug(value) {
  return normalizeText(value)
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function getListingCategoryLabel(listing, categorySlug) {
  const listingCategory = Array.isArray(listing?.listingCategories)
    ? listing.listingCategories
      .map((entry) => entry?.tenantCategory?.name || entry?.category?.name || entry?.coreCategory?.name)
      .map(normalizeText)
      .find(Boolean)
    : '';

  return listingCategory
    || normalizeText(listing?.category)
    || normalizeText(listing?.primaryCategory)
    || titleizeSlug(categorySlug)
    || 'Local business';
}

function getListingHref({ categorySlug, slug, listing }) {
  const publicPath = normalizeText(listing?.publicUrl || listing?.publicPath);
  if (publicPath.startsWith('/') && !publicPath.startsWith('//')) {
    return publicPath.replace(/\/claim\/?$/, '');
  }

  if (categorySlug && slug) {
    return `/listings/${encodeURIComponent(categorySlug)}/${encodeURIComponent(slug)}`;
  }

  if (slug) {
    return `/listings/${encodeURIComponent(slug)}`;
  }

  return '/listings';
}

function getDisplaySlug(slug, listing) {
  return titleizeSlug(listing?.slug || slug);
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeText(value).toLowerCase());
}

function getPhoneDigits(value) {
  return normalizeText(value).replace(/\D/g, '');
}

function getUsPhoneDigits(value) {
  const digits = getPhoneDigits(value);
  if (digits.length === 11 && digits.startsWith('1')) {
    return digits.slice(1);
  }
  return digits.slice(0, 10);
}

function formatUsPhone(value) {
  const digits = getUsPhoneDigits(value);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function isValidPhone(value) {
  return getUsPhoneDigits(value).length === 10;
}

function validateClaimForm(form) {
  const errors = {};
  if (!normalizeText(form.name)) errors.name = 'Enter your name.';
  if (!isValidEmail(form.email)) errors.email = 'Enter a valid email address.';
  if (!isValidPhone(form.phone)) errors.phone = 'Enter a valid 10-digit phone number.';
  if (!normalizeText(form.role)) errors.role = 'Select your role at the business.';
  return errors;
}

export default function ListingClaimPage() {
  const toast = useFeedback();
  const [searchParams] = useSearchParams();
  const promotionCode = searchParams.get('promo') || sessionStorage.getItem('mtsPromotionCode') || '';
  const { categorySlug = '', slug = '' } = useParams();
  const { data: session, isPending: sessionPending } = authClient.useSession();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [fieldErrors, setFieldErrors] = useState({});
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
  });

  useEffect(() => {
    let ignore = false;

    async function loadListing() {
      setLoading(true);
      setError('');

      try {
        const payload = await fetchListingBySlug(slug, { categorySlug });
        if (!ignore) {
          setListing(payload || null);
        }
      } catch (err) {
        if (!ignore) {
          setError(err?.message || 'Unable to load this listing.');
          setListing(null);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    if (!slug) {
      setError('Listing slug is required.');
      setLoading(false);
      return undefined;
    }

    loadListing();
    return () => {
      ignore = true;
    };
  }, [categorySlug, slug]);

  useEffect(() => {
    if (sessionPending) return undefined;

    let ignore = false;
    fetchOwnProfile()
      .then((payload) => {
        if (ignore) return;
        const profile = payload?.profile || null;
        if (!profile?.id && !session?.user?.id) {
          setCurrentUserProfile(null);
          return;
        }

        const nextProfile = {
          id: profile?.id || session?.user?.id || '',
          name: normalizeText(profile?.name || session?.user?.name),
          email: normalizeText(profile?.email || session?.user?.email),
          phone: formatUsPhone(profile?.phone || ''),
        };
        setCurrentUserProfile(nextProfile);
        setForm((current) => ({
          ...current,
          name: nextProfile.name || current.name,
          email: nextProfile.email || current.email,
          phone: nextProfile.phone || current.phone,
        }));
        setFieldErrors((current) => {
          const next = { ...current };
          if (nextProfile.name) delete next.name;
          if (nextProfile.email) delete next.email;
          if (nextProfile.phone) delete next.phone;
          return next;
        });
      })
      .catch(() => {
        if (!ignore && !session?.user?.id) {
          setCurrentUserProfile(null);
        }
      });

    return () => {
      ignore = true;
    };
  }, [sessionPending, session?.user?.id, session?.user?.email, session?.user?.name]);

  const listingHref = useMemo(() => getListingHref({ categorySlug, slug, listing }), [categorySlug, slug, listing]);
  const listingName = normalizeText(listing?.name) || 'This business';
  const listingAddress = buildListingAddress(listing);
  const listingPhone = normalizeText(listing?.phone || listing?.phoneNumber);
  const listingWebsite = normalizeText(listing?.websiteUrl || listing?.website);
  const listingCategory = getListingCategoryLabel(listing, categorySlug);
  const listingSlugLabel = getDisplaySlug(slug, listing);
  const breadcrumbItems = useMemo(() => [
    { label: 'Home', href: '/' },
    { label: 'Listings', href: '/listings' },
    { label: listingCategory, href: '/listings' },
    { label: listingSlugLabel, href: listingHref },
    { label: 'Claim' },
  ], [listingCategory, listingHref, listingSlugLabel]);
  const hasCurrentUserProfile = Boolean(currentUserProfile?.id);

  const updateFormValue = (field, value) => {
    setSubmitted(false);

    setFieldErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
    setForm((current) => ({
      ...current,
      [field]: field === 'phone' ? formatUsPhone(value) : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const errors = validateClaimForm(form);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setSubmitting(false);
      return;
    }
    setSubmitting(true);

    try {
      const payload = await requestListingClaim(listing?.id || slug, {
        name: form.name,
        email: form.email,
        phone: getUsPhoneDigits(form.phone),
        role: form.role,
        ...(promotionCode ? { promotionCode } : {}),
        ...(searchParams.get('priceId') ? { selectedPriceId: searchParams.get('priceId') } : {}),
        ...(searchParams.get('productId') ? { selectedProductId: searchParams.get('productId') } : {}),
      });

      if (payload?.listing) {
        setListing((current) => ({ ...(current || {}), ...payload.listing }));
      }
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.message || 'Unable to submit this claim.', { id: 'action:ListingClaimPage:handleSubmit' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async (event) => {
    event.preventDefault();
    setLoggingOut(true);

    try {
      await authClient.signOut();
    } catch (_error) {
      // Best effort sign-out for UX continuity.
    }
    try {
      await fetch(`${getApiBaseUrl()}/api/auth/portal-token/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (_error) {
      // Best effort sign-out for portal-token sessions.
    }
    resetPostHogIdentity();
    setCurrentUserProfile(null);
    setForm({
      name: '',
      email: '',
      phone: '',
      role: form.role,
    });
    setFieldErrors({});
    setLoggingOut(false);
  };

  if (loading) {
    return (
      <section className="py-8 sm:py-12">
        <PublicContainer>
          <Card className="flex min-h-[18rem] items-center justify-center p-6">
            <span className="loading loading-spinner loading-lg" aria-label="Loading claim page" />
          </Card>
        </PublicContainer>
      </section>
    );
  }

  if (error || !listing) {
    return (
      <section className="py-8 sm:py-12">
        <PublicContainer>
          <Card className="space-y-4 p-6">
            <h1 className="text-2xl font-semibold text-text-primary">Listing not found</h1>
            <p className="text-sm text-text-secondary">{error || 'We could not find the listing you requested.'}</p>
            <Button asChild variant="primary">
              <Link to="/listings">Back to Directory</Link>
            </Button>
          </Card>
        </PublicContainer>
      </section>
    );
  }

  return (
    <section className="py-8 sm:py-12">
      <PublicContainer>
        <PromotionBanner listingId={listing?.id} />
        <PageHeader.Breadcrumbs
          items={breadcrumbItems}
          className="mx-auto mb-5 max-w-5xl"
          aria-label="Claim listing breadcrumb"
        />

        <div className="mx-auto grid max-w-5xl gap-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          <div className="space-y-4">
            <Card className="space-y-5 p-6">
              <div>
                <h1 className="text-3xl font-semibold tracking-tight text-text-primary">{listingName}</h1>
                <p className="mt-2 text-sm text-text-secondary">{listingCategory}</p>
              </div>

              <div className="divide-y divide-border rounded-lg border border-border">
                <div className="p-4">
                  <p className="text-xs font-semibold uppercase text-text-secondary">Address</p>
                  <p className="mt-1 text-sm text-text-primary">{listingAddress || 'Not provided'}</p>
                </div>
                <div className="p-4">
                  <p className="text-xs font-semibold uppercase text-text-secondary">Phone</p>
                  <p className="mt-1 text-sm text-text-primary">{formatPhoneNumber(listingPhone) || 'Not provided'}</p>
                </div>
                <div className="p-4">
                  <p className="text-xs font-semibold uppercase text-text-secondary">Website</p>
                  <p className="mt-1 break-all text-sm text-text-primary">{listingWebsite || 'Not provided'}</p>
                </div>
              </div>
            </Card>

            <Card className="space-y-5 p-6">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                  <Gift size={18} className="text-primary" aria-hidden="true" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-text-primary">What you unlock after claiming</h2>
                  <p className="mt-1 text-sm text-text-secondary">Claiming your listing is free - no credit card required.</p>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="flex items-start gap-3 rounded-md border border-border bg-surface px-3 py-3">
                  <Pencil size={18} className="mt-0.5 shrink-0 text-text-secondary" aria-hidden="true" />
                  <p className="text-sm text-text-secondary">Update business information and hours.</p>
                </div>
                <div className="flex items-start gap-3 rounded-md border border-border bg-surface px-3 py-3">
                  <Camera size={18} className="mt-0.5 shrink-0 text-text-secondary" aria-hidden="true" />
                  <p className="text-sm text-text-secondary">Add photos, offers, and promotions.</p>
                </div>
                <div className="flex items-start gap-3 rounded-md border border-border bg-surface px-3 py-3">
                  <TrendingUp size={18} className="mt-0.5 shrink-0 text-text-secondary" aria-hidden="true" />
                  <p className="text-sm text-text-secondary">Upgrade to Featured or Spotlight.</p>
                </div>
                <div className="flex items-start gap-3 rounded-md border border-border bg-surface px-3 py-3">
                  <Megaphone size={18} className="mt-0.5 shrink-0 text-text-secondary" aria-hidden="true" />
                  <p className="text-sm text-text-secondary">Reach more local customers.</p>
                </div>
              </div>
            </Card>

          </div>

          <Card className="p-6">
            <form className="space-y-5" onSubmit={handleSubmit}>
              <div>
                <h2 className="text-2xl font-semibold text-text-primary">Claim this business</h2>
                <p className="mt-2 text-sm text-text-secondary">Enter your contact details to continue.</p>
              </div>

              {hasCurrentUserProfile ? (
                <div className="rounded-md border border-primary/20 bg-primary/5 px-4 py-3">
                  <p className="text-xs font-semibold uppercase text-text-secondary">Currently Logged In As:</p>
                  <p className="mt-1 text-sm font-medium text-text-primary">
                    {currentUserProfile.name || currentUserProfile.email}
                  </p>
                  {currentUserProfile.email && currentUserProfile.name ? (
                    <p className="mt-0.5 text-xs text-text-secondary">{currentUserProfile.email}</p>
                  ) : null}
                </div>
              ) : null}



              <label className="grid gap-2 sm:grid-cols-[9rem_minmax(0,1fr)] sm:items-start">
                <span className="pt-0 sm:pt-2.5 text-sm font-medium text-text-primary">Name</span>
                <span className="space-y-2">
                  <Input
                    type="text"
                    value={form.name}
                    onChange={(event) => updateFormValue('name', event.target.value)}
                    required
                    autoComplete="name"
                    readOnly={hasCurrentUserProfile}
                    disabled={submitting || submitted}
                    aria-invalid={fieldErrors.name ? 'true' : undefined}
                  />
                  {fieldErrors.name ? <InlineFeedback variant="error" className="block text-xs text-error">{fieldErrors.name}</InlineFeedback> : null}
                </span>
              </label>

              <label className="grid gap-2 sm:grid-cols-[9rem_minmax(0,1fr)] sm:items-start">
                <span className="pt-0 sm:pt-2.5 text-sm font-medium text-text-primary">Email</span>
                <span className="space-y-2">
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(event) => updateFormValue('email', event.target.value)}
                    required
                    autoComplete="email"
                    inputMode="email"
                    readOnly={hasCurrentUserProfile}
                    disabled={submitting || submitted}
                    aria-invalid={fieldErrors.email ? 'true' : undefined}
                  />
                  {fieldErrors.email ? <InlineFeedback variant="error" className="block text-xs text-error">{fieldErrors.email}</InlineFeedback> : null}
                </span>
              </label>

              <label className="grid gap-2 sm:grid-cols-[9rem_minmax(0,1fr)] sm:items-start">
                <span className="pt-0 sm:pt-2.5 text-sm font-medium text-text-primary">Phone</span>
                <span className="space-y-2">
                  <Input
                    type="text"
                    value={form.phone}
                    onChange={(event) => updateFormValue('phone', event.target.value)}
                    required
                    autoComplete="tel"
                    inputMode="numeric"
                    maxLength={14}
                    placeholder="(555) 555-1234"
                    readOnly={hasCurrentUserProfile}
                    disabled={submitting || submitted}
                    aria-invalid={fieldErrors.phone ? 'true' : undefined}
                  />
                  {fieldErrors.phone ? <InlineFeedback variant="error" className="block text-xs text-error">{fieldErrors.phone}</InlineFeedback> : null}
                </span>
              </label>

              <label className="grid gap-2 sm:grid-cols-[9rem_minmax(0,1fr)] sm:items-start">
                <span className="pt-0 sm:pt-2.5 text-sm font-medium text-text-primary">Role</span>
                <span className="space-y-2">
                  <select
                    className="h-11 w-full rounded-md border border-border bg-surface px-3 text-sm text-text-primary shadow-sm outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
                    value={form.role}
                    onChange={(event) => updateFormValue('role', event.target.value)}
                    required
                    disabled={submitting || submitted}
                    aria-invalid={fieldErrors.role ? 'true' : undefined}
                  >
                    {ROLE_OPTIONS.map((option) => (
                      <option key={option.value || 'empty'} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  {fieldErrors.role ? <InlineFeedback variant="error" className="block text-xs text-error">{fieldErrors.role}</InlineFeedback> : null}
                </span>
              </label>

              {hasCurrentUserProfile ? (
                <p className="text-sm text-text-secondary">
                  Not you?{' '}
                  <button
                    type="button"
                    className="font-medium text-primary hover:text-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
                    onClick={handleLogout}
                    disabled={loggingOut || submitting}
                  >
                    {loggingOut ? 'Logging out...' : 'Logout'}
                  </button>
                </p>
              ) : null}

              <div className="flex flex-col gap-3 sm:flex-row">
                <Button type="submit" variant="primary" loading={submitting} disabled={submitting || submitted}>
                  Submit Claim
                </Button>
                <Button asChild type="button" variant="outline">
                  <Link to={listingHref}>Cancel</Link>
                </Button>
              </div>

              {submitted ? (
                <div className="rounded-md border border-success/30 bg-success/10 p-5">
                  <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-success/15 text-success">
                      <CheckCircle2 size={22} aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-lg font-semibold text-text-primary">Claim submitted</h3>
                      <p className="mt-1 text-sm text-text-secondary">
                        We received your claim for {listingName}. Your verification email is on the way.
                      </p>
                    </div>
                  </div>

                  <div className="mt-5 grid gap-3">
                    <div className="flex items-start gap-3 rounded-md border border-success/20 bg-surface px-3 py-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <Mail size={17} aria-hidden="true" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-text-primary">Next Steps: Check your email for next steps</p>
                        <p className="mt-1 text-xs text-text-secondary">Use the secure link to finish verification.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 rounded-md border border-success/20 bg-surface px-3 py-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <UserRound size={17} aria-hidden="true" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-text-primary">Update Your Profile</p>
                        <p className="mt-1 text-xs text-text-secondary">Confirm your contact details after your claim is verified.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 rounded-md border border-success/20 bg-surface px-3 py-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <Sparkles size={17} aria-hidden="true" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-text-primary">Enhance your listing</p>
                        <p className="mt-1 text-xs text-text-secondary">Add photos, offers, and business details customers can trust.</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="rounded-md border border-primary/20 bg-primary/5 p-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10">
                    <ShieldCheck size={16} className="text-primary" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-text-primary">Why verification matters</h3>
                    <p className="mt-1 text-sm text-text-secondary">
                      We verify business ownership to protect local businesses and prevent unauthorized changes.
                    </p>
                  </div>
                </div>
              </div>
            </form>
          </Card>
        </div>
      </PublicContainer>
    </section>
  );
}
